import type { OptionsDocument, QuoteTaxRate } from 'paintscout'

/**
 * For each non-custom tax detail, look up the current description in
 * `options.options.taxRates` by `key` and substitute it. Entries whose key is
 * no longer present in settings pass through unchanged so the line still
 * renders (with whatever stale description was saved on the quote).
 *
 * Rates are not refreshed — silently changing the numeric rate would re-calc
 * totals without the user's knowledge.
 */
export function refreshTaxDetails(taxDetails: QuoteTaxRate[] | undefined, options?: OptionsDocument): QuoteTaxRate[] {
  if (!taxDetails?.length) {
    return taxDetails ?? []
  }
  const taxRates = options?.options?.taxRates
  if (!taxRates?.length) {
    return taxDetails
  }

  return taxDetails.map((tax) => {
    if (!tax.key || tax.key === 'custom') {
      return tax
    }
    const current = taxRates.find((rate) => rate.key === tax.key)
    if (!current || !current.description || current.description === tax.description) {
      return tax
    }
    return { ...tax, description: current.description }
  })
}

/**
 * Comma-joined description matching how `generateTaxRateDetails` builds it,
 * so callers can keep the top-level `tax_description` field in sync after a
 * refresh.
 */
export function joinTaxDescriptions(taxDetails: QuoteTaxRate[]): string {
  return taxDetails
    .map((tax) => tax.description)
    .filter((desc) => !!desc)
    .join(', ')
}
